import {TrueGritStatics} from "../../TrueGritStatics";
import {ISymbol, ISymbolPosition} from "../../../core/reel/ReelInterfaces";
import {TrueGritSymbol, TrueGritSymbolStates} from "../../reels/TrueGritSymbol";
import {gsap, TimelineLite} from "gsap";
import {TrueGrit} from "../../TrueGrit";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TrueGritBottomReelBox} from "./TrueGritBottomReelBox";
import {TrueGritGameMode} from "../../server/TrueGritGameMode";
import {SymbolUtils} from "../../utils/SymbolUtils";
import {isTrueGritStackedWildSymbol} from "../../reels/TrueGritStackedWildSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TrueGritSound} from "../../sound/TrueGritSound";

/**
 * Created by Jerker Nord on 2021-07-08.
 */

export class TrueGritBottomReelView extends PIXI.Container implements StageLayer {
    private readonly _reelId:number;

    private _allInSymbols:ISymbol[] = [];
    private _allOutSymbols:ISymbol[] = [];
    private _collapsedReel:ISymbol[] = [];
    private _shouldFillUp:boolean = false;
    private _boxes:TrueGritBottomReelBox[];
    private _hotZoneLevel:number = 0;

    constructor() {
        super();

        this.name = "Reel 7";
        this._reelId = 7;
    }

    onResize():void {
        this.position = TrueGritStatics.BOTTOM_REEL_VIEW_POSITION
        if(!NolimitApplication.isLandscape) {
            this.position.y = this.position.y - (TrueGritStatics.PORTRAIT_Y_OFFSET + NolimitApplication.screenBounds.top) / 2;
        }
    }

    public clear(){
        while (this._allInSymbols.length > 0) {
            const sym = this._allInSymbols.pop();
            if (sym){
                this.removeAvalancheSymbol(sym);
            }
        }
        while (this._allOutSymbols.length > 0) {
            const sym = this._allOutSymbols.pop();
            if (sym){
                this.removeAvalancheSymbol(sym);
            }
        }
        while (this._collapsedReel.length > 0) {
            const sym = this._collapsedReel.pop();
            if (sym){
                this.removeAvalancheSymbol(sym);
            }
        }
    }

    public initReel(symbols:string[], isAvalancheRestore:boolean):void {
        this._boxes = [];
        let tempLock:TrueGritBottomReelBox;
        for(let i:number = 0; i < 6; i++) {
            tempLock = new TrueGritBottomReelBox(TrueGrit.assetLoader.getTextures("lock2")[0]);
            tempLock.anchor.set(0.5, 0.5);
            tempLock.position.set((tempLock.width / 2) + 10, (tempLock.height / 2) + 10);
            tempLock.visible = false;
            tempLock.isUsed = false;
            this._boxes.push(tempLock);
        }

        this._shouldFillUp = isAvalancheRestore;
        for(let i:number = 0; i < symbols.length; i++) {
            if(!isAvalancheRestore) {
                const symbol:ISymbol = this.createSymbol(symbols[i], TrueGritSymbolStates.NORMAL, {
                    reelId : 7,
                    startPosition : i + 1,
                    targetPosition : i + 1
                });
                this._allOutSymbols.push(symbol);
            } else {
                let restoreBoxLevel:number = 0;
                for(let i:number = 0; i < TrueGrit.model.initData.botTrain.isRevealed.length; i++) {
                    if(TrueGrit.model.initData.botTrain.isRevealed[i]) {
                        restoreBoxLevel++;
                    }
                }
                if(symbols[i] !== "X") {
                    const symbol:ISymbol = this.createSymbol(symbols[i], TrueGritSymbolStates.NORMAL, {
                        reelId : 7,
                        startPosition : i + 1,
                        targetPosition : i + 1
                    });
                    if(i < restoreBoxLevel) {
                        (<TrueGritSymbol>symbol).removeTopChild();
                        (<TrueGritSymbol>symbol).hideSymbol(false);
                    }
                    if(TrueGrit.model.initData.greatBookOfWays[2 + i][4] > 1) {
                        (<TrueGritSymbol>symbol).splitLevel = TrueGrit.model.initData.greatBookOfWays[2 + i][4];
                        (<TrueGritSymbol>symbol).setCurrentStateSpriteTexture();
                    }
                    this._collapsedReel.push(symbol);
                }

                // #446
                this.startHotZoneAnimation(restoreBoxLevel - 1);
            }
        }

        this.onResize();
    }

    public restoreAllBoxes():void {
        this._hotZoneLevel = 0;

        for(let i:number = 0; i < this._boxes.length; i++) {
            if(this._boxes[i].isUsed) {
                this._boxes[i].visible = true;
            }
        }
    }

    public startHotZoneAnimation(level:number):void {
        this._hotZoneLevel = level;
    }

    private checkForBoxExplode(symbol:ISymbol):gsap.core.Timeline {
        const collapseBoxExplodeTimeline:gsap.core.Timeline = gsap.timeline();
        if(!(<TrueGritSymbol>symbol).topChildRemoved) {
            if(symbol.reelTargetPosition <= (this._hotZoneLevel + 1)) {
                collapseBoxExplodeTimeline.add(TrueGrit.symbolBoxView.getCollapsedReelRemoveBoxAnimation(symbol.reelTargetPosition));
                collapseBoxExplodeTimeline.add(()=> {TrueGrit.soundPlayer.playSound(TrueGritSound.TRAIN_REEL_BOX_BREAK_SOUND)}, "<");
                collapseBoxExplodeTimeline.add(() => (<TrueGritSymbol>symbol).removeTopChild(), "-=0.6");
                collapseBoxExplodeTimeline.add(() => (<TrueGritSymbol>symbol).hideSymbol(false), "-=0.6");
            }
        }
        return collapseBoxExplodeTimeline;
    }

    // @ts-ignore
    private getUnusedBox():PIXI.Sprite {
        for(let i:number = 0; i < this._boxes.length; i++) {
            if(!this._boxes[i].isUsed) {
                this._boxes[i].isUsed = true;
                this._boxes[i].visible = true;
                return this._boxes[i];
            }
        }
    }

    private returnUsedBox(returnBox:PIXI.Sprite):void {
        for(let i:number = 0; i < this._boxes.length; i++) {
            if(this._boxes[i] == returnBox) {
                this._boxes[i].isUsed = false;
                this._boxes[i].visible = false;
                break;
            }
        }
    }

    public getSymbolsBySymbolPositions(positions:number[]):ISymbol[] {
        const result = [];
        for(let pos of positions) {
            result.push(this.getSymbolBySymbolPosition(pos));
        }
        return result;
    }

    public getSymbolBySymbolPosition(position:number):ISymbol {
        for(let i:number = 0; i < this._allOutSymbols.length; i++) {
            if(this._allOutSymbols[i].reelTargetPosition == position) {
                return this._allOutSymbols[i];
            }
        }
        throw new Error("True grit bottom reel: Symbol at position " + position + " not found");
    }

    public getAllSymbols(){
        return this._allOutSymbols;
    }

    private createSymbol(symbolName:string, state:TrueGritSymbolStates, symbolPositionData:ISymbolPosition):ISymbol {
        //const symbolState:TrueGritSymbolStates = state == SimpleReelModelState.SPIN || state == SimpleReelModelState.STOP ? TrueGritSymbolStates.SPIN : TrueGritSymbolStates.NORMAL;
        const symbol:TrueGritSymbol = SymbolUtils.createSymbol(symbolName, state, symbolPositionData);
        symbol.position.set((symbolPositionData.startPosition * TrueGritStatics.symbolWidth), 0);
        symbol.reelTargetPosition = symbolPositionData.targetPosition;
        symbol.reelPosition = symbolPositionData.startPosition;
        symbol.reelId = symbolPositionData.reelId;
        this.addChildAt(symbol, 0);
        //Only add boxes on top of symbols if we are in main game
        // #337, #446 -> modified the condition to handle FS/SFS intro restore
        if((TrueGrit.model.data.mode == TrueGritGameMode.NORMAL || TrueGrit.model.data.mode == TrueGritGameMode.NORMAL_AVALANCHE) && !(TrueGrit.model.isRestore && (TrueGrit.model.data.nextMode === TrueGritGameMode.FREESPIN || TrueGrit.model.data.nextMode === TrueGritGameMode.FREESPIN_SUPER))) {
            symbol.topChild = this.getUnusedBox();
            symbol.hideSymbol(true);
        }
        return symbol;
    }

    public getAllOutAnimation():TimelineLite {
        const returnTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        const xNudgeShrinkTl:gsap.core.Timeline = gsap.timeline();
        let counter:number = 3;
        while(this._allOutSymbols.length > 0) {
            const symbol:TrueGritSymbol = <TrueGritSymbol>this._allOutSymbols.pop()!;
            symbol.reelTargetPosition = 4;
            if(symbol == null) {
                debugger;
                throw new Error(`TrueGritBottomReelView.animateAllOut, symbol is null!`);
            } else {
                timeline = gsap.timeline();

                if(isTrueGritStackedWildSymbol(symbol)) {
                    xNudgeShrinkTl.add(symbol.shrink(), 0);
                }

                timeline.add([
                    gsap.timeline({delay : TrueGrit.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TrueGritStatics.bottomReelAllOutBlurDelayFastSpin + (counter * 0.03) : (TrueGritStatics.bottomReelAllOutBlurDelay + (counter * 0.01))}).add((<TrueGritSymbol>symbol).getStateAnimation(TrueGritSymbolStates.SPIN_HORIZONTAL, 0.4)),
                    gsap.to(symbol.position, {
                        duration : TrueGrit.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TrueGritStatics.bottomReelAllOutTotalSpinDurationFastSpin : TrueGritStatics.bottomReelAllOutTotalSpinDuration,
                        x : symbol.position.x - (TrueGritStatics.symbolWidth * (symbol.reelTargetPosition - 1) + 60),
                        ease : "sine.in",
                        delay : TrueGritStatics.topReelSpinStartSymbolDelay * counter
                    })
                ]);

                timeline.add(() => this.removeAvalancheSymbol(symbol));
                subTimelines.push(timeline);
            }
            counter--;
        }
        returnTimeline.add(xNudgeShrinkTl);
        returnTimeline.add(() => TrueGrit.soundPlayer.playTrainReelStartSound());
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public getFillUpReelAnimation(reelSplitReel:string[] = []):gsap.core.Timeline {
        if(this._shouldFillUp) {
            this._shouldFillUp = false;
            let numberOfFillUpSymbols:number = 3 - this._collapsedReel.length;

            for(let i:number = 0; i < numberOfFillUpSymbols; i++) {
                let symbol:ISymbol;
                if(reelSplitReel.length == 0) {
                    symbol = this.createSymbol(TrueGrit.model.gameData.botTrain.symbols[(3 - numberOfFillUpSymbols) + i], TrueGritSymbolStates.SPIN_HORIZONTAL, {
                        reelId : 6,
                        startPosition : 4 + i,
                        targetPosition : (3 - numberOfFillUpSymbols) + i + 1
                    });
                } else {
                    symbol = this.createSymbol(reelSplitReel[(3 - numberOfFillUpSymbols) + i], TrueGritSymbolStates.SPIN_HORIZONTAL, {
                        reelId : 6,
                        startPosition : 4 + i,
                        targetPosition : (3 - numberOfFillUpSymbols) + i + 1
                    });
                }

                this._collapsedReel.push(symbol);
            }
            return this.getFillUpAnimation();
        }
        return gsap.timeline();
    }

    private getFillUpAnimation():gsap.core.Timeline {
        const fillUpTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        let counter:number = 0;
        this._allOutSymbols = [];
        this._collapsedReel.reverse();
        while(this._collapsedReel.length > 0) {
            const symbol:ISymbol = this._collapsedReel.pop()!;
            this._allOutSymbols.unshift(symbol);
            if(symbol == null) {
                debugger;
                throw new Error(`TrueGritBottomReelView.animateFillUp, symbol is null!`);
            } else {
                if(symbol.reelTargetPosition < symbol.reelPosition) {
                    timeline = gsap.timeline();
                    timeline.add([
                        () => {
                            if(symbol.reelPosition - symbol.reelTargetPosition > 1) {
                                gsap.timeline({delay : 0.1}).add((<TrueGritSymbol>symbol).getStateAnimation(TrueGritSymbolStates.LANDING, 0.1))
                            }else{
                                gsap.timeline().add((<TrueGritSymbol>symbol).getStateAnimation(TrueGritSymbolStates.LANDING, 0))
                            }
                        },
                        gsap.to(symbol.position, {
                            duration : 0.1 * (symbol.reelPosition - symbol.reelTargetPosition),
                            x : TrueGritStatics.symbolWidth * symbol.reelTargetPosition,
                            ease : "power3.in",
                            delay : TrueGritStatics.topReelSpinFillUpSymbolDelay * counter
                        })
                    ]);
                    timeline.add(() => {symbol.reelPosition = symbol.reelTargetPosition});
                    timeline.add(() => this.symbolLanding(symbol));
                    timeline.add(this.checkForBoxExplode(symbol));
                    subTimelines.push(timeline);
                }
            }
            counter++;
        }
        fillUpTimeline.add(subTimelines, "+=0.2");
        fillUpTimeline.add(() => TrueGrit.soundPlayer.playTrainReelStopSound());
        fillUpTimeline.add(() => {this._allOutSymbols = this.sortSymbolArray(this._allOutSymbols)});
        return fillUpTimeline;
    }

    /**
     * Calculates which symbols that should move to the left and how many steps
     */
    public collapseBottomReel():gsap.core.Timeline {
        this._collapsedReel = [];
        for(let i:number = 0; i < this._allOutSymbols.length; i++) {
            if(this._allOutSymbols[i].isRemoved) {
                this.removeAvalancheSymbol(this._allOutSymbols[i]);
                this._shouldFillUp = true;
                for(let j:number = (i + 1); j < this._allOutSymbols.length; j++) {
                    if(!this._allOutSymbols[j].isRemoved) {
                        this._allOutSymbols[j].reelTargetPosition--;
                    }
                }
            } else {
                this._collapsedReel.push(this._allOutSymbols[i]);
            }
        }

        return this.getBottomReelCollapseAnimation();
    }

    private getBottomReelCollapseAnimation():gsap.core.Timeline {
        const collapseTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        for(let i:number = 0; i < this._allOutSymbols.length; i++) {
            const symbol:TrueGritSymbol = <TrueGritSymbol>this._allOutSymbols[i];
            if(symbol.reelTargetPosition < symbol.reelPosition) {
                timeline = gsap.timeline();
                if(isTrueGritStackedWildSymbol(symbol)) {
                    timeline.add(symbol.shrink());
                }
                timeline.add([
                    gsap.to(symbol.position, {
                        duration : 0.1 * (symbol.reelPosition - symbol.reelTargetPosition),
                        x : (TrueGritStatics.symbolWidth * symbol.reelTargetPosition),
                        ease : "power3.in",
                        delay : TrueGritStatics.bottomReelSpinStopSymbolDelay * i
                    })]);
                timeline.add(() => {symbol.reelPosition = symbol.reelTargetPosition});
                timeline.add(this.checkForBoxExplode(symbol));
                subTimelines.push(timeline);
            }
        }

        collapseTimeline.add(subTimelines);

        if(collapseTimeline.duration() == 0) {
            collapseTimeline.add(gsap.to(this, {duration : 0.001}));
        }

        return collapseTimeline;
    }

    public getAllInAnimation():gsap.core.Timeline {
        const returnTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        let counter:number = 0;
        this._allOutSymbols = [];
        let playSound:boolean = false;
        while(this._allInSymbols.length > 0) {
            playSound = true;
            const symbol:ISymbol = this._allInSymbols.pop()!;
            this._allOutSymbols.push(symbol);
            if(symbol == null) {
                debugger;
                throw new Error(`TrueGritBottomReelView.animateAllIn, symbol is null!`);
            } else {
                timeline = gsap.timeline({delay : TrueGritStatics.topReelSpinStopSymbolDelay * counter});
                timeline.add([
                    gsap.timeline({delay : TrueGrit.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TrueGritStatics.bottomReelAllInBlurDelayFastSpin : TrueGritStatics.bottomReelAllInBlurDelay}).add((<TrueGritSymbol>symbol).getStateAnimation(TrueGritSymbolStates.LANDING, 0.1)),
                    gsap.to(symbol.position, {
                        duration : TrueGrit.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TrueGritStatics.bottomReelAllInTotalSpinDurationFastSpin : TrueGritStatics.bottomReelAllInTotalSpinDuration,
                        x : (symbol.reelTargetPosition * TrueGritStatics.symbolWidth),
                        ease : "sine.in"
                    })
                ]);
                timeline.add(() => {symbol.reelPosition = symbol.reelTargetPosition});
                subTimelines.push(timeline);
            }
            counter++;
        }
        returnTimeline.add(subTimelines);
        if(playSound) {
            returnTimeline.add(() => TrueGrit.soundPlayer.playTrainReelStopSound());
        }
        return returnTimeline;
    }

    public stop(reelData:string[]):TimelineLite {
        for(let i:number = 0; i < reelData.length - 1; i++) {
            const symbol:ISymbol = this.createSymbol(reelData[i], TrueGritSymbolStates.SPIN_HORIZONTAL, {
                reelId : 7,
                startPosition : 4 + i,
                targetPosition : i + 1
            });
            this._allInSymbols.unshift(symbol);
        }

        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(this.getAllInAnimation());

        return tl;
    }

    private symbolLanding(symbol:ISymbol):void {
        (<TrueGritSymbol>symbol).getStateAnimation(TrueGritSymbolStates.LANDING);
    }

    private sortSymbolArray(toBeSorted:ISymbol[]):ISymbol[] {
        const sortedSymbols:ISymbol[] = [];
        for(let i:number = 1; i < 4; i++) {
            for(let j:number = 0; j < toBeSorted.length; j++) {
                if(toBeSorted[j].reelPosition == i) {
                    sortedSymbols.push(toBeSorted[j]);
                    break;
                }
            }
        }
        return sortedSymbols;
    }

    public removeAvalancheSymbol(symbol:ISymbol) {
        this.returnUsedBox((<TrueGritSymbol>symbol).topChild);
        (<TrueGritSymbol>symbol).removeChild((<TrueGritSymbol>symbol).topChild);
        this.removeChild(<TrueGritSymbol>symbol);
        (<TrueGritSymbol>symbol).destroy({children : true});
    }
}
